import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { CheckCircle, XCircle, TrashSimple, ArrowsClockwise, Warning } from "@phosphor-icons/react";

/**
 * Admin — Shopping Aisle Corrections queue.
 * When users move items to a different aisle in their shopping list, and
 * the correction differs from what the shipped regex classifier would
 * have said, we log it here. Admin sees an aggregated list (name → new
 * aisle, count of unique users) and can promote high-confidence
 * corrections globally so every user benefits.
 */
export default function AisleCorrectionsPanel() {
  const [pending, setPending] = useState([]);
  const [globals, setGlobals] = useState([]);
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/aisle-corrections");
      setPending(data.pending || []);
      setGlobals(data.globals || []);
      setLabels(data.aisle_labels || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load corrections");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const accept = async (row) => {
    const key = `${row.normalised_name}||${row.to_aisle}`;
    setBusyKey(key);
    try {
      const r = await api.post("/admin/aisle-corrections/accept", {
        normalised_name: row.normalised_name,
        to_aisle: row.to_aisle,
      });
      toast.success(`Promoted globally. Retro-moved ${r.data.moved_rows} rows.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't accept");
    } finally { setBusyKey(null); }
  };

  const dismiss = async (row) => {
    const key = `${row.normalised_name}||${row.to_aisle}||dismiss`;
    setBusyKey(key);
    try {
      await api.post("/admin/aisle-corrections/dismiss", {
        normalised_name: row.normalised_name,
        to_aisle: row.to_aisle,
      });
      toast.success("Dismissed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't dismiss");
    } finally { setBusyKey(null); }
  };

  const removeGlobal = async (row) => {
    if (!window.confirm(`Remove the global "${row.normalised_name} → ${row.aisle}" override? Regex default takes over.`)) return;
    const key = `${row.normalised_name}||global`;
    setBusyKey(key);
    try {
      await api.delete(`/admin/aisle-corrections/global/${encodeURIComponent(row.normalised_name)}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove");
    } finally { setBusyKey(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-aisle-corrections-panel">
      <div>
        <h2 className="font-display text-2xl font-black text-rf-ink">Shopping Aisle Corrections</h2>
        <p className="text-sm text-rf-ink-soft mt-1">
          When users move a shopping-list item to a different aisle we log it here. Promote high-confidence corrections
          globally — every user (existing lists included) will benefit immediately.
        </p>
      </div>

      {/* Pending queue */}
      <div className="bg-white rounded-2xl border border-rf-ink/10 overflow-hidden">
        <div className="px-5 py-3 bg-rf-cream-alt/60 border-b border-rf-ink/10 flex items-center gap-2">
          <Warning size={18} className="text-rf-orange" weight="fill" />
          <h3 className="font-bold text-rf-ink">Pending corrections ({pending.length})</h3>
        </div>
        {loading && <p className="p-6 text-center text-rf-ink-soft">Loading…</p>}
        {!loading && pending.length === 0 && (
          <p className="p-6 text-center text-rf-ink-soft">No pending corrections — the classifier is happy.</p>
        )}
        {!loading && pending.length > 0 && (
          <div className="grid grid-cols-12 gap-3 px-5 py-2 border-b border-rf-ink/10 bg-rf-ink/5 text-[10px] uppercase tracking-widest font-black text-rf-ink-soft">
            <div className="col-span-4">Ingredient</div>
            <div className="col-span-3">From → To</div>
            <div className="col-span-2 text-center">Unique users</div>
            <div className="col-span-3 text-right">Actions</div>
          </div>
        )}
        {!loading && pending.map((r) => {
          const key = `${r.normalised_name}||${r.to_aisle}`;
          const busy = busyKey === key || busyKey === `${key}||dismiss`;
          return (
            <div
              key={key}
              className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-rf-ink/5 last:border-0 items-center hover:bg-rf-amber/5"
              data-testid={`aisle-pending-${r.normalised_name}`}
            >
              <div className="col-span-4">
                <p className="font-bold text-rf-ink">{r.display_name || r.normalised_name}</p>
                <p className="text-xs text-rf-ink-soft">key: {r.normalised_name}</p>
              </div>
              <div className="col-span-3 text-sm">
                <span className="text-rf-ink-soft line-through">{labels[r.from_aisle] || r.from_aisle}</span>
                {" → "}
                <span className="font-bold text-rf-orange">{labels[r.to_aisle] || r.to_aisle}</span>
              </div>
              <div className="col-span-2 text-center">
                <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-rf-amber/20 text-rf-orange font-black text-sm">
                  {r.unique_users}
                </span>
              </div>
              <div className="col-span-3 flex justify-end gap-2">
                <button
                  onClick={() => accept(r)}
                  disabled={busy}
                  data-testid={`aisle-accept-${r.normalised_name}`}
                  className="inline-flex items-center gap-1 bg-emerald-600 text-white px-3 py-1.5 rounded-full text-xs font-bold hover:bg-emerald-700 disabled:opacity-50"
                >
                  <CheckCircle size={14} weight="fill" /> Accept globally
                </button>
                <button
                  onClick={() => dismiss(r)}
                  disabled={busy}
                  data-testid={`aisle-dismiss-${r.normalised_name}`}
                  className="inline-flex items-center gap-1 bg-rf-ink/10 text-rf-ink px-3 py-1.5 rounded-full text-xs font-bold hover:bg-rf-ink hover:text-white disabled:opacity-50"
                >
                  <XCircle size={14} /> Dismiss
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Current global overrides */}
      <div className="bg-white rounded-2xl border border-rf-ink/10 overflow-hidden">
        <div className="px-5 py-3 bg-rf-cream-alt/60 border-b border-rf-ink/10 flex items-center gap-2">
          <ArrowsClockwise size={18} className="text-emerald-600" weight="bold" />
          <h3 className="font-bold text-rf-ink">Global overrides in force ({globals.length})</h3>
        </div>
        {globals.length === 0 && (
          <p className="p-6 text-center text-rf-ink-soft">No global overrides yet — regex defaults are in force.</p>
        )}
        {globals.map((g) => (
          <div
            key={g.normalised_name}
            className="flex items-center gap-3 px-5 py-2.5 border-b border-rf-ink/5 last:border-0 hover:bg-rf-amber/5"
            data-testid={`aisle-global-${g.normalised_name}`}
          >
            <span className="font-bold text-rf-ink flex-1">{g.normalised_name}</span>
            <span className="text-sm">→ <span className="font-bold text-rf-orange">{labels[g.aisle] || g.aisle}</span></span>
            <button
              onClick={() => removeGlobal(g)}
              disabled={busyKey === `${g.normalised_name}||global`}
              className="text-rf-ink-soft hover:text-red-600 disabled:opacity-50"
              title="Remove — revert to regex default"
              data-testid={`aisle-global-remove-${g.normalised_name}`}
            >
              <TrashSimple size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
